import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { getReaderByImei, getNearReaders, getReaderById } from '../../services/reader.service';
import { Reader } from '../../types/Reader';
import { isEqual } from 'lodash';
import { GetNearReaderRequest } from '../../requestModels/getReaderRequest';
import { PaginatedResult } from '../../types/PaginatedResult';

interface ReaderState {
  reader?: Reader | null;
  nearReaders: Reader[] | null;
  isReaderLoading: boolean;
  isNearReaderLoading: boolean;
  editReaderData?: Reader | null;
  isCalibration: boolean;
  isEditLockedLocation: boolean;
  error: string | null;
  lastSeenDateTime?: Date | null;
}

const initialState: ReaderState = {
  reader: null,
  isReaderLoading: false,
  nearReaders: null,
  isNearReaderLoading: false,
  editReaderData: null,
  isCalibration: true,
  isEditLockedLocation: false,
  error: null,
};

export const fetchReaderById = createAsyncThunk('readers/getReaderById', async (id: string) => await getReaderById(id));
export const fetchReaderByImei = createAsyncThunk('readers/getReaderByEmei', async (imei: string) => await getReaderByImei(imei));
export const fetchNearReader = createAsyncThunk('readers/getNearReader', async (request: GetNearReaderRequest) => await getNearReaders(request));

export const readerSlice = createSlice({
  name: 'reader',
  initialState,
  reducers: {
    updateNearReaderState: (state, action: PayloadAction<Reader[] | null>) => {
      if (action.payload && action.payload?.length > 0) {
        if (!isEqual(state.nearReaders, action.payload)) {
          state.nearReaders = action.payload;
        }
      }
    },
    updateIsCalibrationState: (state, action: PayloadAction<boolean>) => {
      state.isCalibration = action.payload;
    },

    updateIsEditLockedLocation: (state, action: PayloadAction<boolean>) => {
      state.isEditLockedLocation = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Handle fetch reader by IMEI
    builder
      .addCase(fetchReaderByImei.pending, (state) => {
        state.isReaderLoading = true;
      })
      .addCase(fetchReaderByImei.fulfilled, (state, action: PayloadAction<Reader>) => {
        if (action.payload) {
          state.reader = action.payload;
        }
        state.isReaderLoading = false;
      })
      .addCase(fetchReaderByImei.rejected, (state, action) => {
        state.isReaderLoading = false;
        state.error = action.error.message || 'An unexpected error occurred';
      });

    // Handle fetch reader by ID
    builder
      .addCase(fetchReaderById.pending, (state) => {
        state.isReaderLoading = true;
      })
      .addCase(fetchReaderById.fulfilled, (state, action: PayloadAction<Reader>) => {
        if (action.payload) {
          state.reader = action.payload;
        }
        state.isReaderLoading = false;
      })
      .addCase(fetchReaderById.rejected, (state, action) => {
        state.isReaderLoading = false;
        state.error = action.error.message || 'An unexpected error occurred';
      });

    // Handle fetch near reader
    builder
      .addCase(fetchNearReader.pending, (state) => {
        state.isNearReaderLoading = true;
      })
      .addCase(fetchNearReader.fulfilled, (state, action: PayloadAction<PaginatedResult<Reader>>) => {
        if (action.payload) {
          state.nearReaders = action.payload.results;
        }
        state.isNearReaderLoading = false;
      })
      .addCase(fetchNearReader.rejected, (state, action) => {
        state.isNearReaderLoading = false;
        state.error = action.error.message || 'An unexpected error occurred';
      });
  },
});

export const { updateNearReaderState, updateIsCalibrationState, updateIsEditLockedLocation } = readerSlice.actions;
export default readerSlice.reducer;
